// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; these labels are visual captions for the adjacent control, intentionally not wired via htmlFor (Task #25).
// =============================================================================
// AuditLogPage — paginated viewer for the audit_logs table
// =============================================================================
// Renders: filterable list of audit events (action, user, timestamp, details).
//          Read-only. Audit rows are written by logAudit() across the app.
//
// Pulled out of AdminPages.jsx as part of the Admin file split (May 2026).
// =============================================================================
import React from "react";
import { listAuditLogs } from "../../data/auditLogs";
import { listProfilesByIds } from "../../data/profiles";
import { usePageSize } from "../../hooks/usePageSize";

export function AuditLogPage() {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filterAction, setFilterAction] = React.useState("all");
  // `searchInput` is the field text; `search` is the applied term (applied on
  // Enter / the Search button, so we don't hit the server on every keystroke).
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  // The shared per-device rows-per-page (services/pageSize.ts). This screen pages
  // SERVER-side, so a size change must re-run the query — hence the effect dep.
  const [PAGE_SIZE] = usePageSize();

  function load() {
    setLoading(true);
    const f =
      filterAction !== "all"
        ? FILTERS.find(function (x) {
            return x.value === filterAction;
          })
        : null;
    listAuditLogs({
      page: page,
      pageSize: PAGE_SIZE,
      actionIn: f?.actions || null,
      actionEq: f && !f.actions ? filterAction : null,
      search: search || null,
    }).then(function (logs) {
      setLoading(false);
      if (!logs) return;
      // Fetch profile names for any user_ids in the logs
      const uids = logs
        .map(function (l) {
          return l.user_id;
        })
        .filter(Boolean);
      if (uids.length === 0) {
        setLogs(logs);
        return;
      }
      const unique = uids.filter(function (v, i, a) {
        return a.indexOf(v) === i;
      });
      listProfilesByIds(unique).then(function (rows) {
        const lookup = {};
        if (rows)
          rows.forEach(function (p) {
            lookup[p.id] = p;
          });
        logs.forEach(function (l) {
          l.profiles = lookup[l.user_id] || null;
        });
        setLogs(logs);
      });
    });
  }

  React.useEffect(
    function () {
      load();
    },
    [page, filterAction, search, PAGE_SIZE]
  );

  function applySearch() {
    setPage(0);
    setSearch(searchInput.trim());
  }
  function clearSearch() {
    setSearchInput("");
    setPage(0);
    setSearch("");
  }

  const ACTION_LABELS = {
    approvals_confirmed: "Approvals Confirmed",
    user_updated: "User Updated",
    user_deactivated: "User Deactivated",
    user_reactivated: "User Reactivated",
    standard_costs_updated: "Standard Costs Updated",
    request_submitted: "Request Submitted",
    draft_saved: "Draft Saved",
    draft_deleted: "Draft Deleted",
    comment_sent: "Comment Sent",
    document_uploaded: "Document Uploaded",
    email_send_failed: "Email Send Failed",
    shelter_no_approvals_changed: "DFV Service No-Approvals Flag",
    shelter_primary_user_set: "DFV Service Primary User Set",
    shelter_membership_added: "User Attached to DFV Service",
    shelter_membership_removed: "User Removed from DFV Service",
    delivery_addresses_imported: "Delivery Addresses Imported",
    users_imported: "Users Imported",
    invites_sent: "Invitations Sent",
    admin_permissions_changed: "Admin Permissions Changed",
    lookup_option_changed: "Dropdown List Changed",
  };

  const ACTION_COLOURS = {
    approvals_confirmed: { bg: "#E8F5E9", text: "#2E7D32" },
    user_updated: { bg: "#E3F2FD", text: "#1565C0" },
    user_deactivated: { bg: "#FFEBEE", text: "#C62828" },
    user_reactivated: { bg: "#E8F5E9", text: "#2E7D32" },
    standard_costs_updated: { bg: "#FFF8E1", text: "#F57F17" },
    request_submitted: { bg: "#E8F5E9", text: "#2E7D32" },
    draft_saved: { bg: "#E3F2FD", text: "#1565C0" },
    draft_deleted: { bg: "#FFEBEE", text: "#C62828" },
    comment_sent: { bg: "#E3F2FD", text: "#1565C0" },
    document_uploaded: { bg: "#F3E5F5", text: "#7B1FA2" },
    email_send_failed: { bg: "#FFEBEE", text: "#C62828" },
    shelter_no_approvals_changed: { bg: "#E0F2F1", text: "#00695C" },
    shelter_primary_user_set: { bg: "#E0F2F1", text: "#00695C" },
    shelter_membership_added: { bg: "#E0F2F1", text: "#00695C" },
    shelter_membership_removed: { bg: "#FFF8E1", text: "#F57F17" },
    delivery_addresses_imported: { bg: "#F3E5F5", text: "#7B1FA2" },
    users_imported: { bg: "#F3E5F5", text: "#7B1FA2" },
    invites_sent: { bg: "#E8F5E9", text: "#2E7D32" },
    admin_permissions_changed: { bg: "#EDE7F6", text: "#5E35B1" },
    lookup_option_changed: { bg: "#EDE7F6", text: "#5E35B1" },
  };

  // The filter dropdown is DERIVED from ACTION_LABELS — add a new action there (for
  // its badge) and it appears here automatically; no separate list to maintain. The
  // two multi-action GROUPS below (Drafts, Memberships) collapse a pair of actions
  // into one filter option, emitted in place of their first action. Row badges still
  // show the specific action.
  const FILTER_GROUPS = [
    { value: "drafts", label: "Drafts", actions: ["draft_saved", "draft_deleted"] },
    {
      value: "shelter_memberships",
      label: "DFV Service Memberships",
      actions: ["shelter_membership_added", "shelter_membership_removed"],
    },
  ];
  const groupOf = {};
  FILTER_GROUPS.forEach(function (g) {
    g.actions.forEach(function (a) {
      groupOf[a] = g;
    });
  });
  const seenGroups = new Set();
  const FILTERS = [];
  Object.keys(ACTION_LABELS).forEach(function (action) {
    const g = groupOf[action];
    if (g) {
      if (!seenGroups.has(g.value)) {
        seenGroups.add(g.value);
        FILTERS.push({ value: g.value, label: g.label, actions: g.actions });
      }
    } else {
      FILTERS.push({ value: action, label: ACTION_LABELS[action] });
    }
  });

  function formatDetails(details) {
    if (!details) return "";
    try {
      const obj = typeof details === "string" ? JSON.parse(details) : details;
      return Object.keys(obj)
        .map(function (k) {
          return `${k}: ${obj[k]}`;
        })
        .join(" · ");
    } catch (_e) {
      return details;
    }
  }

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <h2 className="m-0 mb-4 text-[18px] text-zephyr-text">Audit Log</h2>
      <div className="flex gap-[10px] mb-4 items-center">
        <label className="text-[13px] text-zephyr-muted">Filter:</label>
        <select
          value={filterAction}
          onChange={function (e) {
            setFilterAction(e.target.value);
            setPage(0);
          }}
          className="py-[6px] px-[10px] rounded-[7px] border border-zephyr-border text-[13px] bg-white"
          style={{ fontFamily: "inherit" }}
        >
          <option value="all">All Actions</option>
          {FILTERS.map(function (f) {
            return (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            );
          })}
        </select>
        <button
          onClick={load}
          className="bg-zephyr-teal text-white border-none rounded-[7px] py-[6px] px-[14px] text-[13px] font-semibold cursor-pointer"
          style={{ fontFamily: "inherit" }}
        >
          Refresh
        </button>
        <input
          type="text"
          value={searchInput}
          onChange={function (e) {
            setSearchInput(e.target.value);
          }}
          onKeyDown={function (e) {
            if (e.key === "Enter") applySearch();
          }}
          placeholder="Search details / action / request id…"
          className="ml-auto py-[6px] px-[10px] rounded-[7px] border border-zephyr-border text-[13px] bg-white min-w-[220px]"
          style={{ fontFamily: "inherit" }}
        />
        <button
          onClick={applySearch}
          className="bg-zephyr-teal-dark text-white border-none rounded-[7px] py-[6px] px-[14px] text-[13px] font-semibold cursor-pointer"
          style={{ fontFamily: "inherit" }}
        >
          Search
        </button>
        {search ? (
          <button
            onClick={clearSearch}
            className="bg-transparent border border-zephyr-border rounded-[7px] py-[6px] px-[12px] text-[13px] cursor-pointer text-zephyr-muted"
            style={{ fontFamily: "inherit" }}
          >
            Clear
          </button>
        ) : null}
      </div>
      {loading ? (
        <p className="text-zephyr-muted">Loading...</p>
      ) : logs.length === 0 ? (
        <p className="text-zephyr-muted text-sm">No audit logs found.</p>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="bg-zephyr-off-white text-left">
                <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase">
                  Date
                </th>
                <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase">
                  Action
                </th>
                <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase">
                  User
                </th>
                <th className="py-[10px] px-[12px] font-bold text-zephyr-muted text-[11px] uppercase">
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map(function (log, idx) {
                const ac = ACTION_COLOURS[log.action] || { bg: "#F5F5F5", text: "#666" };
                const who = log.profiles
                  ? log.profiles.display_name || log.profiles.staff_user_email || "—"
                  : "—";
                return (
                  <tr
                    key={log.id}
                    className={`${idx % 2 === 0 ? "bg-white" : "bg-zephyr-off-white"} border-t border-zephyr-border`}
                  >
                    <td className="py-[9px] px-[12px] text-zephyr-muted whitespace-nowrap text-xs">
                      {new Date(log.created_at).toLocaleString("en-AU", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="py-[9px] px-[12px]">
                      <span
                        className="rounded-[5px] py-[3px] px-[8px] text-[11px] font-bold"
                        style={{ background: ac.bg, color: ac.text }}
                      >
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                    </td>
                    <td className="py-[9px] px-[12px] text-xs text-zephyr-text">{who}</td>
                    <td className="py-[9px] px-[12px] text-[11px] text-zephyr-muted max-w-[400px] overflow-hidden text-ellipsis whitespace-nowrap">
                      {formatDetails(log.details)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex gap-[10px] mt-[14px] items-center">
        <button
          onClick={function () {
            if (page > 0) setPage(page - 1);
          }}
          disabled={page === 0}
          className={[
            "bg-transparent border border-zephyr-border rounded-[6px] py-[5px] px-[12px] text-xs",
            page > 0 ? "cursor-pointer text-zephyr-text" : "cursor-not-allowed text-zephyr-muted",
          ].join(" ")}
          style={{ fontFamily: "inherit" }}
        >
          ← Prev
        </button>
        <span className="text-xs text-zephyr-muted">Page {page + 1}</span>
        <button
          onClick={function () {
            if (logs.length === PAGE_SIZE) setPage(page + 1);
          }}
          disabled={logs.length < PAGE_SIZE}
          className={[
            "bg-transparent border border-zephyr-border rounded-[6px] py-[5px] px-[12px] text-xs",
            logs.length === PAGE_SIZE
              ? "cursor-pointer text-zephyr-text"
              : "cursor-not-allowed text-zephyr-muted",
          ].join(" ")}
          style={{ fontFamily: "inherit" }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
